import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { Product, ProductBadge } from '@vendorhub/shared/domain/catalog';
import { VhBadgeComponent, type VhBadgeTone } from '../badge/badge.component';
import { VhCardComponent } from '../card/card.component';
import { VhIconComponent } from '../icon/icon.component';
import { VhImageComponent } from '../image/image.component';
import { VhPriceComponent } from '../price/price.component';
import { VhRatingComponent } from '../rating/rating.component';

export type VhProductCardLayout = 'grid' | 'list';

const BADGE_LABEL: Record<ProductBadge, string> = {
  new: 'New',
  featured: 'Featured',
  bestseller: 'Bestseller',
  'low-stock': 'Low stock',
};

const BADGE_TONE: Record<ProductBadge, VhBadgeTone> = {
  new: 'info',
  featured: 'accent',
  bestseller: 'brand',
  'low-stock': 'warning',
};

/**
 * Product tile. Composes card + image + rating + price + badges. Keeps
 * interaction surface thin: favourite and add-to-bag actions bubble as
 * outputs so the parent can decide how to persist them.
 */
@Component({
  selector: 'vh-product-card',
  standalone: true,
  imports: [
    RouterLink,
    VhCardComponent,
    VhImageComponent,
    VhPriceComponent,
    VhRatingComponent,
    VhBadgeComponent,
    VhIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <vh-card variant="elevated" [interactive]="true">
      <div vh-card-media class="vh-product-card__media">
        <a [routerLink]="detailRoute()" class="vh-product-card__media-link">
          <vh-image
            [src]="product().imageUrl"
            [alt]="product().name"
            [aspectRatio]="layout() === 'list' ? '4/3' : '4/3'"
          />
        </a>
        @if (primaryBadge(); as badge) {
          <vh-badge class="vh-product-card__badge" [tone]="badge.tone" size="sm">
            {{ badge.label }}
          </vh-badge>
        }
        <button
          type="button"
          class="vh-product-card__fav"
          [attr.aria-pressed]="favorited()"
          [attr.aria-label]="favorited() ? 'Remove from favourites' : 'Add to favourites'"
          (click)="onToggleFavourite($event)"
        >
          <vh-icon name="heart" size="sm" [strokeWidth]="favorited() ? 2 : 1.6" />
        </button>
      </div>
      <div vh-card-header class="vh-product-card__header">
        <a [routerLink]="vendorRoute()" class="vh-product-card__vendor">{{
          product().vendorName
        }}</a>
        <a [routerLink]="detailRoute()" class="vh-product-card__name">{{ product().name }}</a>
      </div>
      <div class="vh-product-card__body">
        <vh-rating
          [value]="product().rating"
          [count]="product().reviewCount"
          size="sm"
          [showCount]="true"
        />
      </div>
      <div vh-card-footer class="vh-product-card__footer">
        <vh-price
          [price]="product().price"
          [compareAtPrice]="product().compareAtPrice ?? null"
          size="md"
        />
        @if (!product().inStock) {
          <vh-badge tone="neutral" size="sm">Out of stock</vh-badge>
        }
      </div>
    </vh-card>
  `,
  styleUrl: './product-card.component.scss',
  host: {
    '[class]': '"vh-product-card vh-product-card--" + layout()',
  },
})
export class VhProductCardComponent {
  readonly product = input.required<Product>();
  readonly layout = input<VhProductCardLayout>('grid');
  readonly favorited = input(false);

  readonly favoriteToggled = output<{ product: Product; next: boolean }>();

  readonly detailRoute = computed(() => ['/products', this.product().slug]);
  readonly vendorRoute = computed(() => ['/vendors', this.product().vendorSlug]);

  readonly primaryBadge = computed<{ label: string; tone: VhBadgeTone } | null>(() => {
    const b = this.product().badges[0];
    if (!b) return null;
    return { label: BADGE_LABEL[b], tone: BADGE_TONE[b] };
  });

  onToggleFavourite(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.favoriteToggled.emit({ product: this.product(), next: !this.favorited() });
  }
}
